// Pure rules for the public "Sign up your sport club" wizard (see
// `features/company-registration.md`). A visitor registers a Company Admin
// account (proving control of the email address with a passcode), names and
// places the club, adds a description, logo and optional photos — all staged on
// a single `CompanyRegistration` row — and only the final step creates the real
// `Company` (unverified) and `User` rows. This module owns the shared, testable
// rules around that staging row: its statuses, its step gating, the per-step
// input validation and the client projection. The passcode's lifetime and
// attempt budget reuse the OTP rules in `otp.ts`; hashing and randomness stay
// server-side. Zero non-stdlib imports: shared by the wizard UI and the server.

import type { LanguageCode } from './languages';
import {
  COMPANY_DESCRIPTION_MAX,
  COMPANY_NAME_MAX,
  type CompanyPhoto,
  type StoredCompanyPhoto,
} from './types';
import { PASSWORD_MIN, USER_NAME_MAX, isValidEmail } from './users';

/** SPA route of the public sign-up wizard (served the app shell like `/login`). */
export const COMPANY_SIGNUP_ROUTE = '/signup';

/**
 * Lifecycle of a sign-up:
 *
 * - `EMAIL_PENDING` — the admin account details are staged; the emailed
 *   passcode has not been confirmed yet, so every later step is refused.
 * - `EMAIL_VERIFIED` — the registrant proved control of the address; the
 *   company steps (details, profile, photos, finalize) are open.
 * - `SUBMITTED` — the wizard finished: the unverified company and its admin
 *   account exist, and the row is kept only as the record of the sign-up.
 */
export const COMPANY_REGISTRATION_STATUSES = [
  'EMAIL_PENDING',
  'EMAIL_VERIFIED',
  'SUBMITTED',
] as const;

/** A sign-up's lifecycle status. */
export type CompanyRegistrationStatus =
  (typeof COMPANY_REGISTRATION_STATUSES)[number];

/** Whether `value` is a known sign-up status. */
export function isCompanyRegistrationStatus(
  value: unknown,
): value is CompanyRegistrationStatus {
  return (
    typeof value === 'string' &&
    (COMPANY_REGISTRATION_STATUSES as readonly string[]).includes(value)
  );
}

/**
 * A staged company photo: a `StoredCompanyPhoto` minus the owning company,
 * which does not exist yet. Uploaded through the wizard, processed exactly like
 * a company photo (synchronously, watermark-free) and held on the registration
 * row; finalizing turns each into a real `StoredCompanyPhoto` under the new
 * company's id, pointing at the same stored object.
 */
export type StagedCompanyPhoto = Omit<StoredCompanyPhoto, 'companyId'>;

/**
 * The full, server-internal record of one in-progress sign-up. Only hashes of
 * the password and the current passcode are stored, never the secrets
 * themselves — the same discipline as `User.passwordHash` and
 * `RegistrationOtp.codeHash`, so a database leak reveals neither. Map to
 * `CompanyRegistrationView` before returning to a browser.
 */
export interface CompanyRegistration {
  id: string; // Signup_<uuid> — also the wizard's capability
  email: string; // normalized admin email (the verified address)
  adminName: string; // admin display name ('' = none)
  passwordHash: string; // scrypt digest of the chosen password
  language: LanguageCode | null; // UI language the wizard ran in (null = unknown)
  status: CompanyRegistrationStatus;
  codeHash: string | null; // keyed hash of the live passcode (null once verified)
  codeExpiresAt: number | null; // passcode deadline (epoch ms)
  codeAttempts: number; // failed passcode attempts against the live code
  companyName: string; // staged club name ('' until the details step)
  locationId: number | null; // staged dictionary Location id
  description: string; // staged profile text (markdown source, '' = none)
  logoUrl: string | null; // staged public logo URL (null = none)
  photos: StagedCompanyPhoto[]; // staged club photos, oldest first
  companyId: number | null; // the created company, stamped on finalize
  createdAt: number; // when the sign-up started (epoch ms)
}

/**
 * What the wizard itself may see of its sign-up: everything except the hashes
 * and the storage keys. Returned by every step so a reloaded page can resume
 * where it left off.
 */
export interface CompanyRegistrationView {
  id: string;
  email: string;
  adminName: string;
  status: CompanyRegistrationStatus;
  companyName: string;
  locationId: number | null;
  description: string;
  logoUrl: string | null;
  photos: CompanyPhoto[];
}

/** Project a sign-up to what its own wizard may see (no hashes, no storage keys). */
export function toCompanyRegistrationView(
  registration: CompanyRegistration,
): CompanyRegistrationView {
  return {
    id: registration.id,
    email: registration.email,
    adminName: registration.adminName,
    status: registration.status,
    companyName: registration.companyName,
    locationId: registration.locationId,
    description: registration.description,
    logoUrl: registration.logoUrl,
    photos: registration.photos.map((photo) => ({
      id: photo.id,
      url: photo.webpUrl,
      width: photo.width,
      height: photo.height,
      orientation: photo.orientation,
      createdAt: photo.createdAt,
    })),
  };
}

/** The admin-account fields the wizard's first step submits. */
export interface CompanySignupStartInput {
  email: string;
  name?: string;
  password: string;
}

/**
 * Validate the first step's input, returning a map of field -> message for
 * whatever is wrong (empty when valid). Mirrors the rules `UserService`
 * enforces at account creation, so a sign-up that passes here cannot fail
 * user-side at the end of the wizard.
 */
export function validateSignupStart(
  input: CompanySignupStartInput,
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!isValidEmail(input.email ?? '')) {
    errors.email = 'Enter a valid email address.';
  }
  if ((input.name ?? '').trim().length > USER_NAME_MAX) {
    errors.name = `A name must be at most ${USER_NAME_MAX} characters.`;
  }
  if ((input.password ?? '').length < PASSWORD_MIN) {
    errors.password = `A password must be at least ${PASSWORD_MIN} characters.`;
  }
  return errors;
}

/** The company fields the wizard's details step submits. */
export interface CompanySignupDetailsInput {
  companyName: string;
  locationId: number;
}

/**
 * Validate the details step's input, returning a map of field -> message for
 * whatever is wrong (empty when valid). Location *existence* is a store
 * lookup and stays with the service; this checks shape only.
 */
export function validateSignupDetails(
  input: CompanySignupDetailsInput,
): Record<string, string> {
  const errors: Record<string, string> = {};
  const name = (input.companyName ?? '').trim();
  if (name.length === 0) {
    errors.companyName = 'A company name is required.';
  } else if (name.length > COMPANY_NAME_MAX) {
    errors.companyName = `A company name must be at most ${COMPANY_NAME_MAX} characters.`;
  }
  if (!Number.isInteger(input.locationId) || input.locationId < 1) {
    errors.locationId = 'Choose a location.';
  }
  return errors;
}

/**
 * Validate the profile step's description, returning a map of field -> message
 * for whatever is wrong (empty when valid). The description is optional; only
 * its length is bounded, exactly like a company profile edit.
 */
export function validateSignupDescription(
  description: string,
): Record<string, string> {
  if ((description ?? '').length > COMPANY_DESCRIPTION_MAX) {
    return {
      description: `A description must be at most ${COMPANY_DESCRIPTION_MAX} characters.`,
    };
  }
  return {};
}

/**
 * Whether the sign-up may take a post-verification wizard step (details,
 * profile, photos, finalize): the email is verified and the sign-up has not
 * already been submitted.
 */
export function canEditRegistration(
  registration: Pick<CompanyRegistration, 'status'>,
): boolean {
  return registration.status === 'EMAIL_VERIFIED';
}

/**
 * Whether the sign-up is complete enough to finalize: verified, and the
 * details step done (name + location). Description, logo and photos are
 * optional throughout.
 */
export function canFinalizeRegistration(
  registration: Pick<CompanyRegistration, 'status' | 'companyName' | 'locationId'>,
): boolean {
  return (
    canEditRegistration(registration) &&
    registration.companyName.trim().length > 0 &&
    registration.locationId !== null
  );
}
